import {
  ToolbarActionsLane,
  ToolbarMoreActions,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  PrimaryControlsStripToolbar,
  WebPrimaryControlsStripPageControls,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import {
  PrimaryControlsStripBackControl,
  usePrimaryControlsStripBackGate,
  PrimaryControlsStripLeading,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { PrimaryControlsStripExpansionMutePushButton } from "@-/dev.improve.haniverse.routes.nav.app._components.primary-controls-strip.expansion.push-button.mute";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useNavToolbarSlots } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import {
  useLearnMapCoveredOpacityStyle,
  useLearnMapModeSelector,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { type ReactNode } from "react";
import { LearnMapBackButton } from "#learn-map-back-button";
import { LearnStudyEnvironmentPopoverButton } from "#learn-study-environment-popover-button";

function LearnToolbarPanels({ children }: { children: ReactNode }) {
  const { diameter, isLandscape } = useToolbarGridMetrics();
  return (
    <View
      className={
        isLandscape ? "absolute left-2.25 items-center" : "absolute inset-0 items-center pt-2.25"
      }
      pointerEvents="box-none"
      style={isLandscape ? { top: diameter + 18, width: diameter } : undefined}
    >
      {children}
    </View>
  );
}

/**
 * The row (a left column in landscape) never moves when the map opens: the card slides up over it,
 * and while the card covers it the row fades out, uncovering the map's own floating toolbar
 * beneath (`useLearnMapCoveredOpacityStyle`). It fades back in under the returning card. A page
 * opened over the map brings it back at once, to hold that page's controls.
 */
function LearnToolbarSlide({ children }: { children: ReactNode }) {
  const { isLandscape } = useToolbarGridMetrics();
  const isAway = useLearnMapModeSelector((mode) => mode.isMapToolbarCurrent) ?? false;
  const fadeStyle = useLearnMapCoveredOpacityStyle("toolbar");
  return (
    <Animated.View
      accessibilityElementsHidden={isAway}
      className={isLandscape ? "absolute inset-y-0 left-0 z-10" : "z-10"}
      importantForAccessibility={isAway ? "no-hide-descendants" : "auto"}
      pointerEvents={isAway ? "none" : "box-none"}
      style={fadeStyle}
    >
      {children}
    </Animated.View>
  );
}

/**
 * The app shell's top-left slot on Learn: the More menu over the cards, and a Back control over
 * the map (Learn, on the map home). One turns into the other as the cards leave for the map and
 * return.
 */
function LearnToolbarLeadingControl({ more }: { more: ReactNode }) {
  const cardsProgress = useSharedValue(0);
  const progress = useLearnMapModeSelector((mode) => mode.progress) ?? cardsProgress;
  const isMapOpen = useLearnMapModeSelector((mode) => mode.isMapOpen) ?? false;
  const moreStyle = useAnimatedStyle(() => ({
    opacity: 1 - progress.get(),
    transform: [{ scale: interpolate(progress.get(), [0, 1], [1, 0.5]) }],
  }));
  const backStyle = useAnimatedStyle(() => ({
    opacity: progress.get(),
    transform: [{ scale: interpolate(progress.get(), [0, 1], [0.5, 1]) }],
  }));
  return (
    <View className="size-full" pointerEvents="box-none">
      <Animated.View
        accessibilityElementsHidden={isMapOpen}
        className="absolute inset-0"
        importantForAccessibility={isMapOpen ? "no-hide-descendants" : "auto"}
        pointerEvents={isMapOpen ? "none" : "box-none"}
        style={moreStyle}
      >
        {more}
      </Animated.View>
      {/* Where the More trigger sits in the slot: past its 2px leading margin. */}
      <Animated.View
        accessibilityElementsHidden={!isMapOpen}
        className="absolute top-0 left-0.5"
        importantForAccessibility={isMapOpen ? "auto" : "no-hide-descendants"}
        pointerEvents={isMapOpen ? "box-none" : "none"}
        style={backStyle}
      >
        <LearnMapBackButton />
      </Animated.View>
    </View>
  );
}

/**
 * One Learn primary controls strip. The focused screen's controls arrive through the nav toolbar slots — the deck
 * publishes its card controls into the centre — and the study settings share the More tray.
 * Landscape stacks the study controls below More in a left column, leaving recharge below them.
 * The shell's page-control slot holds the More menu at the column's top.
 * Web places the controls in the site header without reserving another row above the cards.
 *
 * While Learn's map is showing, the map's floating toolbar replaces this one (`isCurrent`), and the
 * More menu's slot holds the control that returns to the cards (Back, or Learn on the map home).
 */
export function LearnLayoutToolbar() {
  const areCardsRequested = useLearnMapModeSelector((mode) => mode.areCardsRequested) ?? true;
  const isFocused = useIsFocused();
  if (!areCardsRequested) {
    // The map's entry control is live before either flashcard host or its toolbar is requested.
    if (Platform.OS === "web") {
      return isFocused ? (
        <WebPrimaryControlsStripPageControls>
          <LearnMapBackButton />
        </WebPrimaryControlsStripPageControls>
      ) : null;
    }
    return (
      <PrimaryControlsStripLeading>
        <View className="size-full" pointerEvents="box-none">
          <View className="absolute top-0 left-0.5" pointerEvents="box-none">
            <LearnMapBackButton />
          </View>
        </View>
      </PrimaryControlsStripLeading>
    );
  }
  return <LearnCardsToolbar />;
}

/** The card toolbar's controls are constructed only after the card view has been requested. */
function LearnCardsToolbar() {
  const slots = useNavToolbarSlots();
  const isFocused = useIsFocused();
  const headerBackGate = usePrimaryControlsStripBackGate({ appShellPlacement: "toolbar" });
  const leading =
    slots?.leading ??
    (headerBackGate !== null ? (
      <PrimaryControlsStripBackControl gate={headerBackGate} layout="app-shell" />
    ) : null);
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const isMapToolbarCurrent = useLearnMapModeSelector((mode) => mode.isMapToolbarCurrent) ?? false;
  const actions = (
    <ToolbarMoreActions>
      {slots?.actions}
      <LearnStudyEnvironmentPopoverButton />
      <PrimaryControlsStripExpansionMutePushButton label="Sound" />
    </ToolbarMoreActions>
  );
  if (Platform.OS === "web") {
    return isFocused ? (
      <WebPrimaryControlsStripPageControls>
        <View className="flex-row items-center gap-1" testID="learn-web-header-controls">
          {isMapToolbarCurrent ? (
            <LearnMapBackButton />
          ) : (
            <>
              {leading}
              {actions}
              {slots?.panels}
            </>
          )}
        </View>
      </WebPrimaryControlsStripPageControls>
    ) : null;
  }
  return (
    <LearnToolbarSlide>
      <PrimaryControlsStripToolbar
        className={isLandscape ? "flex-1" : undefined}
        isCurrent={!isMapToolbarCurrent}
        pointerEvents="box-none"
        stripFade={!isLandscape}
        vertical={isLandscape}
      >
        <LearnToolbarPanels>{slots?.panels}</LearnToolbarPanels>
        <ToolbarActionsLane className="gap-1">
          {leading ? (
            <View className="w-6 shrink-0 justify-center" style={{ height: diameter }}>
              {leading}
            </View>
          ) : null}
          {/*
            These push buttons are shared with the header EXPANSION strip, where they are circular
            toggles in a row of their own. Here they are revealed one-per-row inside the disclosure,
            so each is given a `label` at this call site only — the strip keeps its circles.
          */}
          <PrimaryControlsStripLeading>
            <LearnToolbarLeadingControl more={actions} />
          </PrimaryControlsStripLeading>
        </ToolbarActionsLane>
      </PrimaryControlsStripToolbar>
    </LearnToolbarSlide>
  );
}
